import React, { useState, useEffect } from 'react';
import { 
  RefreshCw, 
  Trash2, 
  Mail, 
  Phone, 
  Calendar, 
  User, 
  BookOpen,
  ChevronDown,
  Download,
  Search,
  Filter,
  Lock,
  LogOut,
  Eye,
  EyeOff,
  FileText,
  Users
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import BlogAdmin from '../components/BlogAdmin';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'New', color: 'bg-blue-100 text-blue-800' },
  { value: 'Email Sent', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'Enrolled', color: 'bg-purple-100 text-purple-800' },
  { value: 'Paid', color: 'bg-green-100 text-green-800' },
  { value: 'Course Started', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'Course Completed', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'Certificate Dispatched', color: 'bg-teal-100 text-teal-800' },
  { value: 'Not Activated', color: 'bg-gray-100 text-gray-600' },
];

// Login Component
const LoginForm = ({ onLogin }) => {
  const { toast } = useToast();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        localStorage.setItem('adminToken', data.token);
        localStorage.setItem('adminUser', username);
        onLogin(true);
        toast({
          title: "Welcome!",
          description: "You have successfully logged in.",
        });
      } else {
        toast({
          title: "Login Failed",
          description: data.detail || "Invalid username or password",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Login error:', error);
      toast({
        title: "Error",
        description: "Unable to connect to server",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container" data-testid="login-form">
      <div className="login-card">
        <div className="login-header">
          <div className="login-icon">
            <Lock size={32} />
          </div>
          <h1 className="login-title">Admin Portal</h1>
          <p className="login-subtitle">Hair Extensions Training Academy</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="login-field">
            <label htmlFor="username" className="login-label">Username</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="login-input"
              placeholder="Enter username"
              required
              data-testid="username-input"
            />
          </div>

          <div className="login-field">
            <label htmlFor="password" className="login-label">Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="login-input"
                placeholder="Enter password"
                required
                data-testid="password-input"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="login-button"
            disabled={isLoading}
            data-testid="login-btn"
          >
            {isLoading ? (
              <>
                <RefreshCw className="animate-spin" size={18} />
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

// Main Dashboard Component
const AdminDashboard = () => {
  const { toast } = useToast();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('enquiries');
  const [enquiries, setEnquiries] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [openDropdown, setOpenDropdown] = useState(null);

  // Check if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated && activeTab === 'enquiries') {
      fetchEnquiries();
      fetchStats();
    }
  }, [isAuthenticated]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setIsAuthenticated(false);
    toast({
      title: "Logged Out",
      description: "You have been logged out successfully.",
    });
  };

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/enquiries`);
      if (!response.ok) throw new Error('Failed to fetch enquiries');
      const data = await response.json();
      setEnquiries(data);
    } catch (error) {
      console.error('Error fetching enquiries:', error);
      toast({
        title: "Error",
        description: "Failed to load enquiries",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/enquiries/stats/summary`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const updateStatus = async (enquiryId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/enquiries/${enquiryId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) throw new Error('Failed to update status');

      setEnquiries(prev => 
        prev.map(e => e.id === enquiryId ? { ...e, status: newStatus } : e)
      );
      
      setOpenDropdown(null);
      fetchStats();
      
      toast({
        title: "Status Updated",
        description: `Enquiry status changed to "${newStatus}"`,
      });
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive",
      });
    }
  };

  const deleteEnquiry = async (enquiryId) => {
    if (!window.confirm('Are you sure you want to delete this enquiry?')) return;

    try {
      const response = await fetch(`${API_URL}/api/enquiries/${enquiryId}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete enquiry');

      setEnquiries(prev => prev.filter(e => e.id !== enquiryId));
      fetchStats();
      
      toast({
        title: "Enquiry Deleted",
        description: "The enquiry has been removed",
      });
    } catch (error) {
      console.error('Error deleting enquiry:', error);
      toast({
        title: "Error",
        description: "Failed to delete enquiry",
        variant: "destructive",
      });
    }
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Course', 'Status', 'Message', 'Date'];
    const csvContent = [
      headers.join(','),
      ...filteredEnquiries.map(e => [
        `"${e.name}"`,
        `"${e.email}"`,
        `"${e.phone}"`,
        `"${e.course_name}"`,
        `"${e.status}"`,
        `"${e.message?.replace(/"/g, '""') || ''}"`,
        `"${new Date(e.created_at).toLocaleDateString()}"`,
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `enquiries_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast({
      title: "Export Complete",
      description: `Exported ${filteredEnquiries.length} enquiries to CSV`,
    });
  };

  const getStatusColor = (status) => {
    const option = STATUS_OPTIONS.find(o => o.value === status);
    return option ? option.color : 'bg-gray-100 text-gray-800';
  };

  const filteredEnquiries = enquiries.filter(e => {
    const matchesSearch = 
      e.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.course_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || e.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  // Show login form if not authenticated
  if (!isAuthenticated) {
    return <LoginForm onLogin={setIsAuthenticated} />;
  }

  return (
    <div className="admin-dashboard" data-testid="admin-dashboard">
      {/* Top Header with Logout */}
      <div className="admin-top-bar">
        <h1 className="admin-main-title">Admin Portal</h1>
        <button 
          onClick={handleLogout} 
          className="admin-btn admin-btn-logout"
          data-testid="logout-btn"
        >
          <LogOut size={18} />
          Logout
        </button>
      </div>

      {/* Tabs Navigation */}
      <div className="admin-tabs">
        <button 
          className={`admin-tab ${activeTab === 'enquiries' ? 'active' : ''}`}
          onClick={() => setActiveTab('enquiries')}
          data-testid="enquiries-tab"
        >
          <Users size={18} />
          Enquiries
        </button>
        <button 
          className={`admin-tab ${activeTab === 'blog' ? 'active' : ''}`}
          onClick={() => setActiveTab('blog')}
          data-testid="blog-tab"
        >
          <FileText size={18} />
          Blog Management
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'blog' ? (
        <BlogAdmin />
      ) : (
        <>
          {/* Enquiries Header */}
          <div className="admin-header">
            <div className="admin-header-content">
              <h2 className="admin-title">Enquiries Dashboard</h2>
              <p className="admin-subtitle">Manage course enquiries and student applications</p>
            </div>
            <div className="admin-actions">
              <button 
                onClick={fetchEnquiries} 
                className="admin-btn admin-btn-secondary"
                data-testid="refresh-btn"
              >
                <RefreshCw size={18} />
                Refresh
              </button>
              <button 
                onClick={exportToCSV} 
                className="admin-btn admin-btn-primary"
                data-testid="export-btn"
              >
                <Download size={18} />
                Export CSV
              </button>
            </div>
          </div>

          {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card stat-total">
          <div className="stat-number">{stats.total}</div>
          <div className="stat-label">Total Enquiries</div>
        </div>
        <div className="stat-card stat-new">
          <div className="stat-number">{stats.by_status?.['New'] || 0}</div>
          <div className="stat-label">New</div>
        </div>
        <div className="stat-card stat-enrolled">
          <div className="stat-number">{stats.by_status?.['Enrolled'] || 0}</div>
          <div className="stat-label">Enrolled</div>
        </div>
        <div className="stat-card stat-completed">
          <div className="stat-number">{stats.by_status?.['Course Completed'] || 0}</div>
          <div className="stat-label">Completed</div>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-filters">
        <div className="search-box">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search by name, email or course..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
            data-testid="search-input"
          />
        </div>
        <div className="filter-box">
          <Filter size={18} />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
            data-testid="filter-select"
          >
            <option value="all">All Statuses</option>
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.value}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Enquiries Table */}
      <div className="enquiries-table-container">
        {loading ? (
          <div className="loading-state">
            <RefreshCw className="animate-spin" size={32} />
            <p>Loading enquiries...</p>
          </div>
        ) : filteredEnquiries.length === 0 ? (
          <div className="empty-state">
            <BookOpen size={48} />
            <p>No enquiries found</p>
            {searchTerm || filterStatus !== 'all' ? (
              <button 
                onClick={() => { setSearchTerm(''); setFilterStatus('all'); }}
                className="admin-btn admin-btn-secondary"
              >
                Clear Filters
              </button>
            ) : null}
          </div>
        ) : (
          <table className="enquiries-table" data-testid="enquiries-table">
            <thead>
              <tr>
                <th>Student</th>
                <th>Contact</th>
                <th>Course</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredEnquiries.map((enquiry) => (
                <tr key={enquiry.id} data-testid={`enquiry-row-${enquiry.id}`}>
                  <td>
                    <div className="student-info">
                      <User size={16} />
                      <div>
                        <div className="student-name">{enquiry.name}</div>
                        {enquiry.message && (
                          <div className="student-message" title={enquiry.message}>
                            {enquiry.message.length > 50 
                              ? enquiry.message.substring(0, 50) + '...' 
                              : enquiry.message}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="contact-info">
                      <a href={`mailto:${enquiry.email}`} className="contact-link">
                        <Mail size={14} />
                        {enquiry.email}
                      </a>
                      <a href={`tel:${enquiry.phone}`} className="contact-link">
                        <Phone size={14} />
                        {enquiry.phone}
                      </a>
                    </div>
                  </td>
                  <td>
                    <div className="course-name">{enquiry.course_name}</div>
                  </td>
                  <td>
                    <div className="status-dropdown-container">
                      <button
                        className={`status-badge ${getStatusColor(enquiry.status)}`}
                        onClick={() => setOpenDropdown(openDropdown === enquiry.id ? null : enquiry.id)}
                        data-testid={`status-btn-${enquiry.id}`}
                      >
                        {enquiry.status}
                        <ChevronDown size={14} />
                      </button>
                      {openDropdown === enquiry.id && (
                        <div className="status-dropdown" data-testid={`status-dropdown-${enquiry.id}`}>
                          {STATUS_OPTIONS.map(opt => (
                            <button
                              key={opt.value}
                              className={`status-option ${opt.color}`}
                              onClick={() => updateStatus(enquiry.id, opt.value)}
                              data-testid={`status-option-${opt.value.replace(/\s+/g, '-').toLowerCase()}`}
                            >
                              {opt.value}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="date-info">
                      <Calendar size={14} />
                      {new Date(enquiry.created_at).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </div>
                  </td>
                  <td>
                    <button
                      className="delete-btn"
                      onClick={() => deleteEnquiry(enquiry.id)}
                      title="Delete enquiry"
                      data-testid={`delete-btn-${enquiry.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Results count */}
      {!loading && filteredEnquiries.length > 0 && (
        <div className="results-count">
          Showing {filteredEnquiries.length} of {enquiries.length} enquiries
        </div>
      )}
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
